import React from 'react';
import { FiBookOpen, FiUser, FiLayers, FiEye } from 'react-icons/fi';
import { fetchStudentPlans, fetchStudentPlan } from '../api';
import { useConfirmAlert } from '../hooks/useConfirmAlert';
import { mergePlansToOverview, getMergeStatistics } from '../utils/planMerger';

const StudentPlans = ({ user, onLoadStudentPlan }) => {
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loadingOverview, setLoadingOverview] = React.useState(false);
  const [overviewPlan, setOverviewPlan] = React.useState(null);
  const { alert } = useConfirmAlert();

  // Lade die Pläne für den Lernenden
  const loadStudentPlans = React.useCallback(async () => {
    if (!user?.username) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchStudentPlans(user.username);
      
      if (result.error) {
        setError(result.error);
        setPlans([]);
      } else if (result.success) {
        setPlans(result.plans || []);
      } else {
        setError('Unerwartete Antwort vom Server');
        setPlans([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Lernenden-Pläne:', error);
      setError('Verbindungsfehler beim Laden der Pläne');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  // Initial laden wenn User sich ändert
  React.useEffect(() => {
    if (user?.username) {
      loadStudentPlans();
    }
  }, [user?.username, loadStudentPlans]);

  // Übersichtsplan erstellen
  const createOverviewPlan = React.useCallback(async () => {
    if (!plans || plans.length === 0) return;
    
    setLoadingOverview(true);
    try {
      // Alle Pläne laden
      const planPromises = plans.map(plan => 
        fetchStudentPlan(plan.id, user.username)
      );
      
      const results = await Promise.all(planPromises);
      
      // Nur erfolgreiche Ergebnisse verwenden
      const loadedPlans = results
        .filter(result => result.success && result.plan)
        .map(result => result.plan);
      
      if (loadedPlans.length === 0) {
        await alert('Keine Pläne konnten geladen werden', { title: "Fehler" });
        return;
      }
      
      // Pläne zusammenführen
      const merged = mergePlansToOverview(loadedPlans);
      setOverviewPlan(merged);
      
    } catch (error) {
      console.error('Fehler beim Erstellen des Übersichtsplans:', error);
      await alert('Fehler beim Erstellen des Übersichtsplans: ' + error.message, { title: "Fehler" });
    } finally {
      setLoadingOverview(false);
    }
  }, [plans, user?.username, alert]);

  // Übersichtsplan automatisch erstellen wenn Pläne geladen wurden
  React.useEffect(() => {
    if (plans.length > 0 && !overviewPlan && !loadingOverview) {
      createOverviewPlan();
    }
  }, [plans, overviewPlan, loadingOverview, createOverviewPlan]);

  // Übersichtsplan laden
  const handleLoadOverviewPlan = async () => {
    if (!overviewPlan) {
      await createOverviewPlan();
      return;
    }
    
    if (onLoadStudentPlan) {
      onLoadStudentPlan(overviewPlan);
    }
  };

  // Einzelplan laden
  const handleLoadPlan = async (plan) => {
    try {
      setLoading(true);
      
      const result = await fetchStudentPlan(plan.id, user.username);
      
      if (result.error) {
        await alert(`Fehler beim Laden des Plans: ${result.error}`, { title: "Fehler" });
        return;
      }
      
      if (result.success && result.plan) {
        // Rufe die übergebene Callback-Funktion auf, um den Plan zu laden
        if (onLoadStudentPlan) {
          onLoadStudentPlan(result.plan);
        }
      } else {
        await alert('Plan konnte nicht geladen werden', { title: "Fehler" });
      }
    } catch (error) {
      console.error('Fehler beim Laden des Plans:', error);
      await alert('Verbindungsfehler beim Laden des Plans', { title: "Verbindungsfehler" });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div style={{ 
      borderTop: '1px solid #ddd', 
      marginTop: '16px',
      backgroundColor: '#fafafa'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 8px',
        backgroundColor: '#e3f2fd',
        borderBottom: '1px solid #2196f3',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#1976d2',
        fontWeight: '600',
        fontSize: '14px'
      }}>
        <FiUser size={16} />
        Meine zugeordneten Pläne
        {plans.length > 0 && (
          <span style={{
            backgroundColor: '#2196f3',
            color: 'white',
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '11px',
            minWidth: '18px',
            textAlign: 'center',
            lineHeight: '14px'
          }}>
            {plans.length}
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ 
        backgroundColor: '#ffffff',
        minHeight: '200px',
        maxHeight: '400px',
        overflow: 'auto'
      }}>
        {loading && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '14px'
          }}>
            Lade Pläne...
          </div>
        )}

        {/* Übersichtsplan - Prominente Anzeige */}
        {!loading && !error && plans.length > 0 && (
          <div style={{ 
            padding: '12px 16px',
            borderBottom: '2px solid #4caf50',
            backgroundColor: '#e8f5e8',
            cursor: 'pointer',
            position: 'relative'
          }}
            onClick={handleLoadOverviewPlan}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#d4edda'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#e8f5e8'}
          >
            {/* Übersichts-Badge */}
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '12px',
              backgroundColor: '#4caf50',
              color: 'white',
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '8px',
              fontWeight: 'bold'
            }}>
              ÜBERSICHT
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '6px'
            }}>
              <FiLayers 
                size={18} 
                style={{ 
                  color: '#2e7d32',
                  flexShrink: 0 
                }} 
              />
              <div style={{
                fontWeight: '600',
                fontSize: '15px',
                color: '#1b5e20',
                flex: 1,
                paddingRight: '60px' // Platz für Badge
              }}>
                {overviewPlan ? overviewPlan.name : 'Übersichtsplan'}
                {loadingOverview && ' (wird erstellt...)'}
              </div>
            </div>
            
            <div style={{
              fontSize: '12px',
              color: '#2e7d32',
              marginBottom: '4px',
              fontWeight: '500'
            }}>
              {overviewPlan ? (() => {
                const stats = getMergeStatistics(overviewPlan);
                return `${stats.sourcePlans.length} Pläne → ${stats.totalGroups} Gruppen, ${stats.totalPanels} Panels${stats.mergedGroups > 0 ? ` (${stats.mergedGroups} zusammengeführt)` : ''}`;
              })() : `${plans.length} ${plans.length === 1 ? 'Plan' : 'Pläne'} zusammengeführt`}
            </div>
            
            <div style={{
              fontSize: '11px',
              color: '#666',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>
                {overviewPlan && overviewPlan.assigned_tasks_count ? 
                  `${overviewPlan.assigned_tasks_count} Tasks insgesamt` : 
                  'Alle zugeordneten Tasks'}
              </span>
              <span style={{ 
                color: '#4caf50',
                fontWeight: '500'
              }}>
                ← Standardansicht
              </span>
            </div>
            
            {loadingOverview && (
              <div style={{
                marginTop: '8px',
                padding: '6px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                fontSize: '11px',
                color: '#666',
                textAlign: 'center'
              }}>
                Pläne werden zusammengeführt...
              </div>
            )}
          </div>
        )}

        {/* Trennlinie wenn Übersichtsplan vorhanden */}
        {!loading && !error && plans.length > 1 && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            fontSize: '12px',
            fontWeight: '500',
            color: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <FiEye size={12} />
            Einzelne Pläne
          </div>
        )}

        {error && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#d32f2f',
            fontSize: '14px',
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            margin: '10px',
            borderRadius: '4px'
          }}>
            {error}
            <div style={{ marginTop: '10px' }}>
              <button
                onClick={loadStudentPlans}
                style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        )}

        {!loading && !error && plans.length === 0 && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '14px'
          }}>
            Ihnen sind noch keine Pläne zugeordnet.
            <div style={{ 
              fontSize: '12px', 
              marginTop: '8px', 
              color: '#999',
              lineHeight: '1.4' 
            }}>
              Wenden Sie sich an Ihre Lehrperson, um Zugang zu Plänen zu erhalten.
            </div>
          </div>
        )}

        {!loading && !error && plans.length > 0 && (
          <div style={{ padding: '8px 0' }}>
            {plans.map((plan) => (
              <div
                key={plan.id}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  opacity: plans.length === 1 ? 0.7 : 1, // Weniger prominent wenn nur ein Plan
                  ':hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={() => handleLoadPlan(plan)}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <FiBookOpen 
                    size={16} 
                    style={{ 
                      color: '#4caf50',
                      flexShrink: 0 
                    }} 
                  />
                  <div style={{
                    fontWeight: '500',
                    fontSize: '14px',
                    color: '#333',
                    flex: 1
                  }}>
                    {plan.name}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#666',
                    backgroundColor: '#f0f0f0',
                    padding: '2px 6px',
                    borderRadius: '3px'
                  }}>
                    v{plan.version}
                  </div>
                </div>
                
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>
                    {plan.assigned_tasks_count} {plan.assigned_tasks_count === 1 ? 'Task' : 'Tasks'} zugeordnet
                  </span>
                  <span style={{ color: '#999' }}>
                    von {plan.user_id}
                  </span>
                </div>
                
                {plan.description && (
                  <div style={{
                    fontSize: '12px',
                    color: '#888',
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    {plan.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPlans;